import { useId, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  apiLoadErrorText,
  useGetPrcBreakEvenQuery,
  type PrcModelRow,
  type PrcVersionPayload,
} from '@shared/api';
import { useDebouncedValue } from '@shared/lib/useDebouncedValue';
import { Select } from '@shared/ui/Input';
import { SkeletonLine } from '@shared/ui/Skeleton';
import { blockNumber, fleetLeading, fleetType, normalizeDecimal, typo } from '@entities/fleet';
import { compareDecimal, shiftDecimal } from '@entities/prc';
import { FieldError, FieldHint, FieldLabel, Notice, TextButton } from '@widgets/FleetRegistry/ui';
import {
  GridInput,
  NARROW,
  Section,
  SectionHead,
  SectionNote,
  SectionTitle,
  SegmentButton,
  Segmented,
  Stack,
  Toolbar,
  YearSwitch,
} from '../ui';
import { BreakEvenChart } from './BreakEvenChart';
import {
  BreakEvenInputs,
  BreakEvenKpis,
  BreakEvenNeed,
  BreakEvenVerdict,
  SensitivityTable,
} from './breakEvenParts';
import { FormButtons } from './ModelDrawer';

const Controls = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 2fr) auto minmax(0, 1fr);
  gap: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(5)}`};
  align-items: end;

  @container prc (max-width: 960px) {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }

  @container prc (max-width: ${NARROW}) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const VariantNote = styled.p`
  margin: ${({ theme }) => `${theme.spacing(2)} 0 0`};
  max-width: 88ch;
  font-size: ${fleetType.small};
  line-height: ${fleetLeading.normal};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-wrap: pretty;
`;

const SensInput = styled(GridInput)`
  max-width: 120px;
`;

const Buttons = styled(Toolbar)`
  margin-top: ${({ theme }) => theme.spacing(4)};
`;

const Body = styled.div<{ $stale: boolean }>`
  opacity: ${({ $stale }) => ($stale ? 0.55 : 1)};
  transition: opacity ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.easeOut};
`;

const VARIANT_NOTE: Readonly<Record<1 | 2, string>> = {
  1: 'Вариант 1 «коммерческий»: общецеховые входят в переменные затраты часа, постоянные — только амортизация в месяц.',
  2: 'Вариант 2 «управленческий», как в образце книги: переменные — без общецеховых, постоянные — амортизация плюс общецеховые × плановая загрузка.',
};

/** Модели с расчётом — по категориям, для выбора машины. */
function byCategory(models: readonly PrcModelRow[]): { title: string; models: PrcModelRow[] }[] {
  const out: { title: string; models: PrcModelRow[] }[] = [];
  for (const m of models) {
    if (m.error) continue;
    const title = m.category_name ?? 'Без категории';
    const last = out[out.length - 1];
    if (last && last.title === title) last.models.push(m);
    else out.push({ title, models: [m] });
  }
  return out;
}

/**
 * Вкладка «Безубыточность» (блоки 1–4 «Калькуляции (форма)»): выбор машины,
 * вариант 1/2, изменение для чувствительности. Показатели, вывод, график с
 * таблицей и сценарии. Это аналитика — в справочник техники не пишется.
 */
export function BreakEvenTab({
  payload,
  modelId,
  onModel,
  year,
  onYear,
  variant,
  onVariant,
}: {
  payload: PrcVersionPayload;
  modelId: string | null;
  onModel: (id: string) => void;
  year: number;
  onYear: (year: number) => void;
  variant: 1 | 2;
  onVariant: (variant: 1 | 2) => void;
}) {
  const uid = useId();
  const v = payload.version;
  const groups = useMemo(() => byCategory(payload.models), [payload.models]);
  const computed = groups.flatMap((g) => g.models);
  const current = computed.find((m) => m.model_id === modelId) ?? computed[0] ?? null;
  const [sens, setSens] = useState('10');
  const sensNorm = normalizeDecimal(sens);
  const sensValid =
    sensNorm !== null &&
    (compareDecimal(sensNorm, '0') ?? -1) >= 0 &&
    (compareDecimal(sensNorm, '1000') ?? 1) <= 0;
  const share = sensValid && sensNorm ? shiftDecimal(sensNorm, -2) : null;
  const debounced = useDebouncedValue(share, 400);

  const { data, error, isFetching, isLoading, refetch } = useGetPrcBreakEvenQuery(
    {
      id: v.id,
      modelId: current?.model_id ?? '',
      variant,
      year,
      sensitivity: debounced ?? undefined,
      conditions: payload.variant?.id,
    },
    { skip: !current },
  );
  const shown = data && data.model.model_id === current?.model_id ? data : undefined;

  if (!current) {
    return (
      <Notice $tone="info">
        <span className="text">
          {typo('В версии нет посчитанных моделей — безубыточность не по чему считать.')}
        </span>
      </Notice>
    );
  }

  return (
    <>
      <Controls>
        <div>
          <FieldLabel htmlFor={`${uid}-model`}>Машина</FieldLabel>
          <Select
            id={`${uid}-model`}
            value={current.model_id}
            aria-describedby={`${uid}-model-hint`}
            onChange={(e) => onModel(e.target.value)}
          >
            {groups.map((g) => (
              <optgroup key={g.title} label={g.title}>
                {g.models.map((m) => (
                  <option key={m.model_id} value={m.model_id}>
                    {m.model_name}
                  </option>
                ))}
              </optgroup>
            ))}
          </Select>
          <FieldHint id={`${uid}-model-hint`}>
            {typo(
              payload.summary.errors > 0
                ? `Модели с ошибкой расчёта (${payload.summary.errors}) в списке не показаны.`
                : 'Все модели версии посчитаны.',
            )}
          </FieldHint>
        </div>
        <div>
          <FieldLabel as="span" id={`${uid}-variant`}>
            Вариант расчёта
          </FieldLabel>
          <Segmented role="group" aria-labelledby={`${uid}-variant`}>
            <SegmentButton type="button" aria-pressed={variant === 1} onClick={() => onVariant(1)}>
              1 · коммерческий
            </SegmentButton>
            <SegmentButton type="button" aria-pressed={variant === 2} onClick={() => onVariant(2)}>
              2 · управленческий
            </SegmentButton>
          </Segmented>
        </div>
        <div>
          <FieldLabel htmlFor={`${uid}-sens`}>Изменение для чувствительности, %</FieldLabel>
          <SensInput
            id={`${uid}-sens`}
            value={sens}
            inputMode="decimal"
            autoComplete="off"
            aria-invalid={sensValid ? undefined : true}
            aria-describedby={sensValid ? undefined : `${uid}-sens-err`}
            onChange={(e) => setSens(e.target.value)}
          />
          {!sensValid && (
            <FieldError id={`${uid}-sens-err`}>От 0 до 1 000 %, например 10</FieldError>
          )}
        </div>
      </Controls>
      <VariantNote>{typo(VARIANT_NOTE[variant])}</VariantNote>
      <Buttons>
        <YearSwitch
          years={v.params.years}
          baseYear={v.params.base_year}
          value={year}
          onChange={onYear}
        />
        <FormButtons
          version={v}
          modelId={current.model_id}
          modelName={current.model_name}
          print={{
            year,
            variant,
            sensitivity: share ?? undefined,
            conditions: payload.variant?.id,
          }}
        />
      </Buttons>

      {error !== undefined && !shown ? (
        <Section>
          <Notice $tone="danger" role="alert">
            <span className="text">
              {apiLoadErrorText(
                error,
                'Не удалось посчитать безубыточность.',
                'Нет доступа к ПРЦ.',
              )}
            </span>
            <TextButton type="button" onClick={() => void refetch()}>
              Повторить
            </TextButton>
          </Notice>
        </Section>
      ) : isLoading || !shown ? (
        <Section aria-busy="true">
          <SkeletonLine $height={56} />
          <SkeletonLine $height={88} />
          <SkeletonLine $height={260} />
        </Section>
      ) : (
        <Body $stale={isFetching} aria-busy={isFetching}>
          <Section aria-labelledby={`${uid}-b2`}>
            <SectionHead>
              <SectionTitle id={`${uid}-b2`}>
                <span className="n" aria-hidden="true">
                  {blockNumber(0)}
                </span>
                Результат
              </SectionTitle>
              <SectionNote>
                {typo(`${shown.model.model_name} · ${shown.year} год · вариант ${shown.variant}`)}
              </SectionNote>
            </SectionHead>
            <Stack>
              <BreakEvenVerdict data={shown} />
            </Stack>
            <BreakEvenKpis data={shown} />
            <BreakEvenNeed data={shown} />
          </Section>

          <Section aria-labelledby={`${uid}-b3`}>
            <SectionHead>
              <SectionTitle id={`${uid}-b3`}>
                <span className="n" aria-hidden="true">
                  {blockNumber(1)}
                </span>
                График безубыточности
              </SectionTitle>
              <SectionNote>Выручка и затраты при загрузке 20…260 ч в месяц, тыс. ₽</SectionNote>
            </SectionHead>
            <BreakEvenChart data={shown} />
          </Section>

          <Section aria-labelledby={`${uid}-b1`}>
            <SectionHead>
              <SectionTitle id={`${uid}-b1`}>
                <span className="n" aria-hidden="true">
                  {blockNumber(2)}
                </span>
                Исходные данные
              </SectionTitle>
            </SectionHead>
            <BreakEvenInputs data={shown} />
          </Section>

          <Section aria-labelledby={`${uid}-b4`}>
            <SectionHead>
              <SectionTitle id={`${uid}-b4`}>
                <span className="n" aria-hidden="true">
                  {blockNumber(3)}
                </span>
                Чувствительность
              </SectionTitle>
              <SectionNote>
                {typo(
                  'Что будет с безубыточностью, если цена, переменные или постоянные затраты изменятся',
                )}
              </SectionNote>
            </SectionHead>
            <SensitivityTable data={shown} />
          </Section>
        </Body>
      )}
    </>
  );
}
